import type { CSSProperties } from "react";
import { useHudStore } from "../store/hudStore";
import Page from "../components/Page";
import AddQuestForm from "../components/AddQuestForm";
import CompactRow from "../components/CompactRow";
import { servesMain } from "../lib/quests";
import CertsModule from "../components/modules/CertsModule";
import { Badge, Button, Card, Progress } from "../design-system/components";
import type { MainQuest } from "../types";

// Main-quest page (app.html screenMain, 805-832). `empty` used to be hardcoded
// true, carried over from the source where the drill-in lists were demo-only.
// That made this page permanently show no quests and a 0% bar no matter what
// was filed under the main quest. It now follows live data, and the percentage
// uses the same rule as the HUD card (selectors.ts mainStat): the goal's own
// `progress` when set, else its child quests, else untracked.
export default function Main() {
  const st = useHudStore();
  const empty = !st.live;
  const fallback: MainQuest = { id: st.main, name: "Main quest", note: "", eta: "", color: "var(--accent)", fa: "fa-solid fa-flag" };
  const m = st.mains.find((x) => x.id === st.main) || st.mains[0] || fallback;
  const all = st.quests.filter((q) => servesMain(q, m.id));
  const finished = all.filter((q) => st.done[q.id]);
  const leftXp = all.filter((q) => !st.done[q.id]).reduce((t, q) => t + q.xp, 0);
  const explicit = typeof m.progress === "number" && Number.isFinite(m.progress);
  const pct = explicit
    ? Math.max(0, Math.min(100, Math.round(m.progress as number)))
    : all.length
      ? Math.round((finished.length / all.length) * 100)
      : 0;
  const tracked = !empty && (explicit || all.length > 0);
  const quests = empty ? [] : all;
  const color = m.color || "var(--accent)";
  // Some main quests are tracked by more than their quests. The Golden Jacket is
  // twelve `cert` entities, and only whichever exam is in flight shows up as a
  // quest, so this page read "0 of 1" while eleven other exams sat unlisted.
  const hasCerts = !empty && st.certs.some((c) => c.goal === m.id);

  return (
    <Page back="All main quests">
      <Card className="detail-hero">
        <span className="detail-identity-icon" style={{ color }}><i aria-hidden="true" className={m.fa || "fa-solid fa-flag"} /></span>
        <div className="detail-head-body">
          <div className="detail-title-row"><h1 className="detail-heading detail-title">{m.name}</h1><Badge>Main quest</Badge></div>
          {m.note && <p className="detail-copy">{m.note}</p>}
          <Progress value={tracked ? pct : 0} label="Main quest progress" valueLabel={all.length > 0 ? `${finished.length} of ${all.length} quests done · ${leftXp} XP left` : explicit ? `${pct}% done · no quests filed yet` : "No quests yet"} style={{ "--ds-tone": color } as CSSProperties} />
          {m.eta && <p className="detail-copy detail-target">{m.eta}</p>}
        </div>
      </Card>
      {hasCerts && <CertsModule goal={m.id} />}
      <Card title="Quests under this main quest" actions={<Button size="compact" onClick={() => st.openAddForMain(m.id)} icon="fa-solid fa-plus">Add here</Button>}>
        {st.adding && <AddQuestForm />}
        {quests.map((q) => <CompactRow key={q.id} quest={q} showDomainChip />)}
        {quests.length === 0 && <p className="detail-empty">No quests under this main quest yet. Add the first concrete step.</p>}
      </Card>
    </Page>
  );
}
